/**
 * The person's parallel agents in the prompt box: "Agente 1", "Agente 2"… Each is a lane of the prompt
 * queue — the desktop app runs every lane in its own agent at the same time, one prompt per lane (see
 * localRunner.ts and the server's PromptQueueService). Agent 1 is the usual one and is always there;
 * the "+" opens the next number free, up to {@link MAX_LANES}.
 *
 * Which agents are open is remembered on this browser; what each one is doing comes from the server.
 */

/** As the server's PromptQueueRules.MaxLanes. */
export const MAX_LANES = 6

const LANES_KEY = 'devpulse.promptDock.lanes'

/** The open agents, in order; always starts with 1. */
export function normaliseLanes(value: unknown): number[] {
  const numbers = Array.isArray(value) ? value.filter((n): n is number => Number.isInteger(n) && n >= 1 && n <= MAX_LANES) : []
  return [...new Set([1, ...numbers])].sort((a, b) => a - b)
}

export function readLanes(): number[] {
  try {
    return normaliseLanes(JSON.parse(localStorage.getItem(LANES_KEY) ?? '[1]'))
  } catch {
    return [1]
  }
}

export function saveLanes(lanes: number[]): void {
  try {
    localStorage.setItem(LANES_KEY, JSON.stringify(lanes))
  } catch {
    // Private storage: the agents stay open until the page closes.
  }
}

/** The next agent's number: the smallest one not open, or null when {@link MAX_LANES} are. */
export function nextLane(lanes: number[]): number | null {
  for (let n = 1; n <= MAX_LANES; n++) if (!lanes.includes(n)) return n
  return null
}

/** Closes an agent's tab; the first agent stays. */
export function withoutLane(lanes: number[], lane: number): number[] {
  return lane === 1 ? lanes : lanes.filter((n) => n !== lane)
}

/** Which agent a queued prompt is for: 1 when the server does not say. */
export function laneOf(item: { lane?: number | null }): number {
  return item.lane && item.lane > 1 ? item.lane : 1
}
